import _, {uniqBy} from 'lodash';
import {
  NORMALIZATION_STATE_TASK_PROPERTIES,
  SERIAL_DILUTION_SDPROPERTY_PROPERTIES,
} from '../../constants/cardStateEnum';
import {CardType, NormalizationType, SerialDilutionStep, TaskType} from '../../constants/dataEnum';
import {cloneAndMapObject, cloneAndMapObjectWithDataTypes, findObjectValueByKeys} from '../../lib/objectHelper';
import {generateSdRawTasksAndSummary, mapToSerialDilutionStep} from '../../lib/serialDilutionHelper';

// this is the new normalization format after fixed diluent volume normalization type is added
export const mapNewNormalizationStateFormat = (normalizationState) =>
  cloneAndMapObject(
    {
      ...normalizationState,
      type: NormalizationType.FIXED_SAMPLE_VOLUME,
      diluentWell: {
        row: normalizationState.selectedDiluentWell.row,
        column: normalizationState.selectedDiluentWell.column,
      },
      sampleLabwareID: normalizationState.selectedLabware?.LabwareID,
      diluentLabwareID: normalizationState.selectedDiluentWell.labware.LabwareID,
    },
    NORMALIZATION_STATE_TASK_PROPERTIES,
  );

// for pre-revamped serial dilution (1.21.0.0 and below)
export const syncPreRevampedSerialDilutionSdProperty = (sdCard) => {
  const {sdRawTasks, sdRawTasksSummary, sdProperty} = sdCard;
  const {sampleVolume, diluentVolume, step, ratio} = sdProperty;
  const {taskDiluentTargets} = sdRawTasksSummary;

  let newSdProperty = cloneAndMapObjectWithDataTypes(sdProperty, SERIAL_DILUTION_SDPROPERTY_PROPERTIES, true);

  newSdProperty = {
    ...newSdProperty,
    sampleVolume: Number(diluentVolume) + Number(sampleVolume),
    aliquotVolume: Number(sampleVolume),
  };

  for (let i = 0; i < step; i += 1) {
    const iStep = i + 1;

    const rawTaskId = taskDiluentTargets[i];
    const currentRawTask = sdRawTasks[rawTaskId];
    currentRawTask.step = iStep;

    newSdProperty.stepProperties.push({
      step: iStep,
      stepSampleVolume: Number(sampleVolume),
      stepDiluentVolume: Number(diluentVolume),
      ratio: [1, Number(ratio)],
    });
  }

  return newSdProperty;
};

const findSDEqualTask = (task, sdRawTask) => {
  if (!sdRawTask) return null;

  return (
    task.type === sdRawTask.type &&
    task.column === sdRawTask.column &&
    task.row === sdRawTask.row &&
    task.labwareID === sdRawTask.labwareID &&
    task.number === sdRawTask.number
  );
};

export const standardizeSdRawTasks = (sdCard, tasksState) => {
  const {tasks: sdTasks, sdRawTasksSummary, sdRawTasks, sdProperty} = sdCard;

  const isSerialDilutionPreRevamped = Boolean(!sdProperty.aliquotVolume);

  const sampleTargetTaskNumber = isSerialDilutionPreRevamped
    ? SerialDilutionStep.SET_DILUENT_TARGET
    : SerialDilutionStep.SET_SAMPLE_TARGET;

  const serialDiluentTasks = findObjectValueByKeys(sdTasks, tasksState);
  const {
    taskDiluentSource: diluentSourceID,
    taskSampleSource: sampleSourceID,
    taskSampleTarget: sampleTargetID,
    taskDiluentTargets: diluentTargetIDs,
  } = sdRawTasksSummary;

  const sampleTargetIDs = _.isArray() ? sampleTargetID : [sampleTargetID];

  // rawTask region
  const diluentSourceSdRawTask = sdRawTasks[diluentSourceID];
  const sampleSourceSdRawTask = sdRawTasks[sampleSourceID];
  const sampleTargetSdRawTask = findObjectValueByKeys(sampleTargetIDs, sdRawTasks);
  const diluentTargetSdRawTask = findObjectValueByKeys(diluentTargetIDs, sdRawTasks);

  // processed task region
  const diluentSourceTask = serialDiluentTasks.find((task) => findSDEqualTask(task, diluentSourceSdRawTask));
  const sampleSourceTask = serialDiluentTasks.find((task) => findSDEqualTask(task, sampleSourceSdRawTask));

  let sampleTargetTask = serialDiluentTasks?.filter(
    (task) =>
      task.number === sampleTargetTaskNumber &&
      sampleTargetSdRawTask.find((rawTask) =>
        findSDEqualTask(task, {...rawTask, number: sampleTargetTaskNumber, type: TaskType.ASPIRATE}),
      ),
  );

  if (!isSerialDilutionPreRevamped) {
    const isMultiQuadrant = sampleTargetIDs.length > 1;

    sampleTargetTask = isMultiQuadrant ? sampleTargetTask : uniqBy(sampleTargetTask, ['number']);
  }

  const diluentTargetTask = serialDiluentTasks.filter(
    (task) =>
      task.number === SerialDilutionStep.SET_DILUENT_TARGET &&
      diluentTargetSdRawTask.find((rawTask) => findSDEqualTask(task, rawTask)),
  );

  const adjustedDiluentTargetTask =
    sdProperty.replicateWell > 1
      ? [
          {
            ...diluentTargetTask[0],
            rowTipCount: diluentTargetSdRawTask[0].rowTipCount,
            columnTipCount: diluentTargetSdRawTask[0].columnTipCount,
          },
          ...diluentTargetTask.slice(1),
        ]
      : diluentTargetTask;

  // mapped the processed task
  const mappedRawTask = mapToSerialDilutionStep(
    diluentSourceTask,
    sampleSourceTask,
    sampleTargetTask,
    adjustedDiluentTargetTask,
  );

  // generate ordered rawTasks and summaries
  const {sdRawTasks: newSdRawTasks, sdRawTasksSummary: newSdRawSummary} = generateSdRawTasksAndSummary(
    mappedRawTask,
    sdProperty.step,
  );

  // synchronized sdProperty
  const alteredTasks = {};
  let newSdProperty = sdProperty;

  if (isSerialDilutionPreRevamped) {
    newSdProperty = syncPreRevampedSerialDilutionSdProperty(sdCard);
    sampleTargetTask.forEach((task) => {
      alteredTasks[task.ID] = {...task, number: SerialDilutionStep.SET_SAMPLE_TARGET};
    });
  }

  return {
    alteredTasks,
    alteredCard: {
      ...sdCard,
      sdProperty: newSdProperty,
      sdRawTasks: newSdRawTasks,
      sdRawTasksSummary: newSdRawSummary,
    },
  };
};

export const convertOldStateContentFormat = (stateContent) => {
  const {cardState} = stateContent;
  const {cards, tasks} = cardState;

  const newCards = {};
  let newTasks = {...tasks};

  Object.values(cards).forEach((card) => {
    const cardID = card.ID;
    newCards[cardID] = {...card};

    const currentCard = newCards[cardID];

    if (card.type === CardType.NORMALIZATION) {
      const {normalizationState} = currentCard;
      const isOldNormalizationFormat = normalizationState.selectedDiluentWell;

      if (isOldNormalizationFormat) {
        newCards[cardID].normalizationState = mapNewNormalizationStateFormat(normalizationState);
      }
    } else if (card.type === CardType.SERIAL_DILUTION) {
      const {alteredTasks, alteredCard} = standardizeSdRawTasks(currentCard, tasks);

      newTasks = {...newTasks, ...alteredTasks};
      newCards[cardID] = alteredCard;
    }
  });

  const newCardState = {
    ...cardState,
    cards: newCards,
    tasks: newTasks,
  };

  return {...stateContent, cardState: newCardState};
};
